import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { BudgetForecastSegment } from 'lumen/models';
import {
  DATA_TYPE_TRANSLATIONS,
  type DataType,
} from 'lumen/utils/constants/budget-forecast-constants';
import type { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';
import safeDivide from 'lumen/utils/numeric/safe-divide';

export type ResourceDataType = 'budget' | 'forecast';
type DateAggregation =
  | typeof DATE_AGGREGATION.DAY
  | typeof DATE_AGGREGATION.MONTH;

export type Meta = {
  totals: Partial<
    Record<DataType, { adr: number; revenue: number; rooms: number }>
  >;
};

export default class BudgetForecastDataItem extends Model {
  [Type] = 'budget-forecast/data-item' as const;

  @belongsTo('budget-forecast/data-item', { async: false, inverse: null })
  declare budget: BudgetForecastDataItem;

  @belongsTo('budget-forecast/data-item', { async: false, inverse: null })
  declare forecast: BudgetForecastDataItem;

  @belongsTo('budget-forecast/data-item', { async: false, inverse: 'children' })
  declare parent: BudgetForecastDataItem;

  @belongsTo('budget-forecast/segment', { async: false, inverse: 'dataItems' })
  declare segment: BudgetForecastSegment | null;

  @hasMany('budget-forecast/data-item', { async: false, inverse: 'parent' })
  declare children: HasMany<BudgetForecastDataItem>;

  /** Revenue value from the API */
  @attr('number') declare _revenue: number;
  @attr('number') declare adr: number;
  @attr('string') declare dataType: DataType;
  @attr('dayjs') declare date: NonNullable<DayjsTransformReturn>;
  @attr('string') declare dateAggregation: DateAggregation;
  @attr('number') declare rooms: number;

  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];

  @tracked isItemHidden = false;
  @tracked needsForceDistribution = false;
  @tracked wasChangedByPushPayload = false;

  /**
   * Backend distribution endpoint only distributes downwards.
   * So we need to do the upwards summing of children in frontend.
   */
  get #shouldSumChildren(): boolean {
    if (this.hasUndistributedChanges || this.needsForceDistribution) {
      return false;
    }

    return (
      this.hasChildrenWithUndistributedChanges ||
      this.hasChildrenWithUnsavedChanges
    );
  }

  get adrToShow(): number {
    if (!this.#shouldSumChildren) {
      return this.adr;
    }

    return safeDivide(this.revenueToShow, this.roomsToShow) ?? 0;
  }

  get depth(): number {
    if (this.parent) {
      return this.parent.depth + 1;
    }

    return 0;
  }

  get filteredChildren(): BudgetForecastDataItem[] {
    return this.children.filter((child) => !child.isItemHidden);
  }

  get formattedDate(): string {
    const dateFormats = {
      day: this.dates.partialDateFormat.WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT,
      month: this.dates.partialDateFormat.YEAR_AND_MONTH_LONG,
    };

    return this.date.format(dateFormats[this.dateAggregation]);
  }

  get formattedSegment(): string {
    if (this.segment) {
      return this.segment.name;
    }

    if (this.isRoot) {
      return this.intl.t(DATA_TYPE_TRANSLATIONS[this.dataType]);
    }

    if (this.isUnmapped) {
      return this.intl.t('general.unmapped');
    }

    return '';
  }

  get hasChildrenWithUndistributedChanges(): boolean {
    return this.filteredChildren.some(
      (child) =>
        child.hasUndistributedChanges ||
        child.hasChildrenWithUndistributedChanges,
    );
  }

  get hasChildrenWithUnsavedChanges(): boolean {
    return this.filteredChildren.some(
      (child) => child.hasUnsavedChanges || child.hasChildrenWithUnsavedChanges,
    );
  }

  get hasData(): boolean {
    return this.rooms !== 0 || this.adr !== 0;
  }

  get hasParentWithUndistributedChanges(): boolean {
    if (!this.parent) {
      return false;
    }

    return (
      this.parent.hasUndistributedChanges ||
      this.parent.hasParentWithUndistributedChanges
    );
  }

  get hasUndistributedChanges(): boolean {
    // Changes on the lowest granularity don't need to be distributed downwards.
    if (this.isLowestGranularity) {
      return false;
    }

    return this.wereChangesMade;
  }

  get hasUnsavedChanges(): boolean {
    if (this.wasChangedByPushPayload) {
      return true;
    }

    if (this.isLowestGranularity) {
      return this.wereChangesMade;
    }

    return false;
  }

  get isItemForHiddenSegment(): boolean {
    if (this.isRoot || !this.segment) {
      return false;
    }

    if (this.dataType !== 'budget' && this.dataType !== 'forecast') {
      return false;
    }

    // If the segment has dirty attributes, check the original value.
    // We do this to avoid the UI updating immediately.
    if (this.segment.hasDirtyAttributes) {
      const changedAttributes = this.segment.changedAttributes();
      if (changedAttributes['visible']) {
        return !changedAttributes['visible'][0];
      }
    }

    return !this.segment.visible;
  }

  get isLowestGranularity(): boolean {
    return (
      this.children.length === 0 ||
      this.children.every(
        (child) => child.isItemForHiddenSegment && !child.hasData,
      )
    );
  }

  get isRoot(): boolean {
    return this.parent === null;
  }

  get isUnmapped(): boolean {
    return this.segment === null && !this.isRoot;
  }

  /**
   * Users can only fill in the rooms and ADR fields.
   * We want the revenue to be calculated instantly when the user changes the rooms or ADR.
   * Hence why we calculate it in frontend instead of backend.
   * For non-editable dataTypes, use _revenue to handle edge cases like cancellations (where rooms * ADR might not equal revenue)
   */
  get revenue(): number {
    if (this.dataType === 'budget' || this.dataType === 'forecast') {
      return this.rooms * this.adr;
    }
    return this._revenue ?? 0;
  }

  get revenueToShow(): number {
    if (!this.#shouldSumChildren) {
      return this.revenue;
    }

    return this.filteredChildren.reduce(
      (acc, child) => acc + child.revenueToShow,
      0,
    );
  }

  get roomsToShow(): number {
    if (!this.#shouldSumChildren) {
      return this.rooms;
    }

    return this.filteredChildren.reduce(
      (acc, child) => acc + child.roomsToShow,
      0,
    );
  }

  get roundedAdr(): number {
    return Math.round(this.adrToShow * 100) / 100;
  }

  get roundedRevenue(): number {
    return Math.round(this.revenueToShow);
  }

  get roundedRooms(): number {
    return Math.round(this.roomsToShow * 100) / 100;
  }

  /**
   * Returns a unique key for the segment or segment-alike (e.g. 'root' or 'unmapped:root').
   */
  get segmentKey(): string {
    if (this.isRoot) {
      return 'root';
    }

    if (this.segment) {
      return this.segment.id!;
    }

    // Multiple unmapped segments can exist, though only one per parent.
    // We suffix the parent segment key to ensure uniqueness.
    return `unmapped:${this.parent.segmentKey}`;
  }

  get wereChangesMade(): boolean {
    return Boolean(this.hasDirtyAttributes);
  }

  rollbackAttributes(): void {
    super.rollbackAttributes();

    this.wasChangedByPushPayload = false;
    this.isItemHidden = false;
    this.needsForceDistribution = false;
  }
}
